import React, { useReducer, useEffect, useContext } from "react";
import moduleConfig from "./moduleConfig";
import { Card, Row, Col } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import ModuleHeader from "../../UI/modules/views/partials/common/ModuleHeader";
import {
  createReducer,
  createInitialState,
} from "../../../reducers/module/createReducer";
import { BreadcrumbContext } from "../../../contexts/BreadcrumbContext";
import DashboardLayout from "../../UI/layouts/DashboardLayout";
import FormErrorMessage from "../../UI/errorMessages/FormErrorMessage";
import CMSFormInput from "../../UI/forms/CMSFormInput";
import BlockErrorMessage from "../../UI/errorMessages/BlockErrorMessage";
import CMSFormSelect from "../../UI/forms/CMSFormSelect";
import CMSFormRichText from "../../UI/forms/CMSFormRichText";
import CMSFormImage from "../../UI/forms/CMSFormImage";
import { onSubmitGeneral } from "../../../helpers/ModuleHelperFunctions";
import CMSFormColor from "../../UI/forms/CMSFormColor";
import CMSFormImageWithCrop from "../../UI/forms/CMSFormImageWithCrop";

const SpotLightAreaCreate = () => {
  const [createState, createDispatch] = useReducer(
    createReducer,
    createInitialState
  );
  const { setBreadcrumb } = useContext(BreadcrumbContext);
  let navigate = useNavigate();

  useEffect(() => {
    setBreadcrumb([
      moduleConfig.breadcrumbs.listing,
      moduleConfig.breadcrumbs.create,
    ]);
  }, []);
  let imageRequirement = {
    dimension: {
      width: 500,
      height: 500,
    },
    allowedExtensions: ["image/png", "image/jpg", "image/jpeg"],
    size: 1024 * 1024 * 3,
  };
  return (
    <DashboardLayout>
      {createState.status === "fetchFailed" ? (
        <BlockErrorMessage error={createState.error} />
      ) : (
        <>
          {/* {console.log(createState.data)} */}
          {createState.status !== "fetching" && (
            <>
              <ModuleHeader
                moduleTitle={`Create ${moduleConfig.singleItemTitle}`}
                moduleUrl={moduleConfig.url}
              ></ModuleHeader>

              <section className="content">
                <Card>
                  <Card.Body>
                    {createState.isLoading && (
                      <div className="overlay position-absolute containerLoading">
                        <i className="fas fa-2x fa-sync-alt fa-spin"></i>
                      </div>
                    )}
                    <Formik
                      initialValues={{
                        position: "Left", //'Left','Center','Right'
                        slideBackgroundColor: "#a1b031",
                        heading1: "",
                        heading1Color: "#31160d",
                        heading2: "",
                        heading2Color: "#31160d",
                        buttonText: "",
                        buttonTextColor: "#31160d",
                        buttonLink: "",
                        buttonBackgroundColor: "#d26e32",
                        buttonIcon: "Plus",
                        file: "",
                        imagePosition: "Left",
                        imageSize: "Large",
                        sortOrder: "",
                        status: "0",
                      }}
                      validationSchema={yup.object({
                        position: yup
                          .string()
                          .required("Position is required."),

                        slideBackgroundColor: yup
                          .string()
                          .required("Slide background color is required."),

                        heading1: yup
                          .string()
                          .required("Heading 1 is required.")
                          .max(10, "Heading 1 cannot exceed 10 characters."),

                        heading1Color: yup
                          .string()
                          .required("Heading 1 color is required."),

                        heading2: yup
                          .string()
                          .required("Heading 2 is required.")
                          .max(10, "Heading 2 cannot exceed 10 characters."),

                        heading2Color: yup.string().when("heading2", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required("Heading 2 color is required."),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonText: yup.string(),

                        buttonTextColor: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required("Button text color is required."),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonLink: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema
                              .required("Button link is required.")
                              .url("Please enter a valid URL."),
                          otherwise: (schema) =>
                            schema
                              .notRequired()
                              .url("Please enter a valid URL."),
                        }),

                        buttonBackgroundColor: yup.string().when("buttonText", {
                          is: (val) => val && val.trim() !== "",
                          then: (schema) =>
                            schema.required(
                              "Button background color is required."
                            ),
                          otherwise: (schema) => schema.notRequired(),
                        }),

                        buttonIcon: yup.string(),

                        imagePosition: yup
                          .string()
                          .required("Image position is required."),

                        imageSize: yup
                          .string()
                          .required("Image size is required."),

                        file: yup.mixed().when("position", {
                          is: (pos) => pos !== "Center", // if NOT center → file REQUIRED
                          then: (schema) =>
                            schema
                              .required("Please upload an image.")
                              .imageTypeCheck(
                                moduleConfig.imageRequirement.allowedExtensions
                              )
                              .imageSizeCheck(
                                moduleConfig.imageRequirement.size
                              ),
                          otherwise: (schema) => schema.notRequired(), // if center → file NOT required
                        }),

                        sortOrder: yup
                          .number()
                          .typeError("Sort order must be a number.")
                          .required("Sort order is required."),

                        status: yup.string().required("Status is required."),
                      })}
                      enableReinitialize={true}
                      validateOnChange={true}
                      validateOnBlur={true}
                      onSubmit={async (values, { resetForm }) => {
                        if (values.position === "Center") {
                          delete values.file;
                        }
                        const formData = new FormData();
                        for (let name in values) {
                          if (Array.isArray(values[name])) {
                            formData.append(name, JSON.stringify(values[name]));
                          } else {
                            formData.append(name, values[name]);
                          }
                        }
                        await onSubmitGeneral(
                          formData,
                          moduleConfig.url,
                          moduleConfig.url,
                          createDispatch,
                          navigate
                        );
                      }}
                    >
                      {(form) => (
                        <Form onSubmit={form.handleSubmit}>
                          {createState.error && (
                            <FormErrorMessage error={createState.error} />
                          )}

                          {/* {console.log(form.errors)} */}

                          <CMSFormSelect
                            name="position"
                            label="Position"
                            required={true}
                            form={form}
                            options={[
                              { label: "Left", value: "Left" },
                              { label: "Center", value: "Center" },
                              { label: "Right", value: "Right" },
                            ]}
                          />

                          <CMSFormColor
                            name="slideBackgroundColor"
                            label="Background Color"
                            required={true}
                            form={form}
                          />

                          <CMSFormInput
                            name="heading1"
                            label="Heading 1"
                            required={true}
                            form={form}
                          />

                          <CMSFormColor
                            name="heading1Color"
                            label="Heading 1 Color"
                            required={true}
                            form={form}
                          />

                          <CMSFormInput
                            name="heading2"
                            label="Heading 2"
                            required={true}
                            form={form}
                          />

                          <CMSFormColor
                            name="heading2Color"
                            label="Heading 2 Color"
                            required={form.values.heading2 ? true : false}
                            form={form}
                          />

                          <CMSFormInput
                            name="buttonText"
                            label="Button Text"
                            required={false}
                            form={form}
                          />

                          <CMSFormColor
                            name="buttonTextColor"
                            label="Button Text Color"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormInput
                            name="buttonLink"
                            label="Button Link"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormColor
                            name="buttonBackgroundColor"
                            label="Button Color"
                            required={form.values.buttonText ? true : false}
                            form={form}
                          />

                          <CMSFormSelect
                            name="buttonIcon"
                            label="Button Icon"
                            required={false}
                            form={form}
                            options={[
                              { label: "Right Arrow", value: "Right Arrow" },
                              { label: "Plus", value: "Plus" },
                            ]}
                          />
                          {form.values.position != "Center" && (
                            <CMSFormImageWithCrop
                              label="Image"
                              name="file"
                              info="Recommended dimensions 500*500"
                              form={form}
                              aspect={1 / 1}
                              outputSize={imageRequirement.dimension}
                              required={
                                form.values.position != "Center" ? true : false
                              }
                            />
                          )}

                          <CMSFormSelect
                            name="imagePosition"
                            label={
                              form.values.position != "Center"
                                ? "Image Position"
                                : "Product Image Position"
                            }
                            required={true}
                            form={form}
                            options={[
                              { label: "Right", value: "Right" },
                              { label: "Left", value: "Left" },
                            ]}
                          />

                          <CMSFormSelect
                            name="imageSize"
                            label={
                              form.values.position != "Center"
                                ? "Image Size"
                                : "Product Image Size"
                            }
                            required={true}
                            form={form}
                            options={[
                              { label: "Small", value: "Small" },
                              { label: "Large", value: "Large" },
                            ]}
                          />

                          <CMSFormInput
                            name="sortOrder"
                            label="Sort Order"
                            form={form}
                            required={true}
                          />

                          <CMSFormSelect
                            name="status"
                            label="Status"
                            required={true}
                            form={form}
                            options={[
                              { label: "Select Status...", value: "" },
                              { label: "Enabled", value: "1" },
                              { label: "Disabled", value: "0" },
                            ]}
                          />

                          <button
                            type="submit"
                            onClick={() => form.handleSubmit}
                            className="btn btn-primary"
                          >
                            Submit
                          </button>
                        </Form>
                      )}
                    </Formik>
                  </Card.Body>
                </Card>
              </section>
            </>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default SpotLightAreaCreate;
